import { useEffect, useState } from 'react'
import { useAppSelector } from '../../../../../store/hooks'
import { selectAccountId } from '../../../../../store/auth/authSlice'
import { useGetAccountQuery } from '../../../../../store/accountUserSettings/accountUserSettings'
import { type CheckpointRequest, useCreateCheckpointMutation, useGetCheckpointQuery, useUpdateCheckpointMutation } from '../../../../../store/sync/sync'
import { useGetMeQuery, useUpdateUserIntegrationMutation } from '../../../../../store/user/user'
import { useGetIntegrationEntitiesQuery } from '../../../../../store/integrations/integrations'
import { IntegrationServiceId } from '../../../../../common/integrations'
import { type MultiSelectOption } from '../../../../../components/MultiSelectDropdown/MultiSelectDropdown'
import { getAccountCRMIntegrationConfig } from '../../../../../models/Account'
import { merge } from '../../../utils'
import { removeEmptyItems } from '../../../../../common/util'
import { type RedtailInboundContactSyncSettingsFilters, type RedtailSettingsFormValues } from './model'

export const useRedtailContactSync = () => {
  const accountId = useAppSelector(selectAccountId)
  const { data: account, isFetching: isAccountFetching } = useGetAccountQuery({ accountId }, { refetchOnMountOrArgChange: 10 })
  const checkpointRequest = {
    accountId,
    level: 'user',
    connector: 'redtail',
    direction: 'inbound',
    object: 'contact_operation_hashed',
  } as CheckpointRequest
  const { data: checkpoint, isFetching: isGetCheckpointFetching } = useGetCheckpointQuery(checkpointRequest)
  const [createCheckpoint, { isLoading: isCreateCheckpointLoading }] = useCreateCheckpointMutation()
  const [updateCheckpoint, { isLoading: isUpdateCheckpointLoading }] = useUpdateCheckpointMutation()
  const [isSyncDisabled, setIsSyncDisabled] = useState<boolean>(true)
  const { data: userData, isFetching: isGetMeQueryFetching } = useGetMeQuery({ accountId: accountId })
  const [updateUserIntegration, { isLoading: isUpdateUserIntegrationLoading }] = useUpdateUserIntegrationMutation()
  const [isUpdating, setIsUpdating] = useState<boolean>(false)

  const [contactCategoryOptions, setContactCategoryOptions] = useState<MultiSelectOption[] | null>(null)
  const [contactStatusOptions, setContactStatusOptions] = useState<MultiSelectOption[] | null>(null)
  const [contactTypeOptions, setContactTypeOptions] = useState<MultiSelectOption[] | null>(null)
  const [tierCategoriesSelectOptions, setTierCategoriesSelectOptions] = useState<MultiSelectOption[] | null>(null)

  const { data: contactCategoriesEntity, isFetching: isContactCategoriesFetching } = useGetIntegrationEntitiesQuery({
    accountId,
    connector: IntegrationServiceId.REDTAIL,
    entityType: 'contact_categories',
  })

  const { data: contactStatusesEntity, isFetching: isContactStatusesFetching } = useGetIntegrationEntitiesQuery({
    accountId,
    connector: IntegrationServiceId.REDTAIL,
    entityType: 'contact_statuses',
  })

  const { data: contactTypesEntity, isFetching: isContactTypesFetching } = useGetIntegrationEntitiesQuery({
    accountId,
    connector: IntegrationServiceId.REDTAIL,
    entityType: 'contact_types',
  })

  useEffect(() => {
    if (account && account.data) {
      const serviceIntegration = getAccountCRMIntegrationConfig(account.data, 'redtail')
      setIsSyncDisabled(serviceIntegration?.isSyncDisabled ?? false)
    }
  }, [account])

  useEffect(() => {
    if (!contactCategoriesEntity) {
      return
    }
    const { contactCategories } = contactCategoriesEntity.data
    if (!contactCategories) {
      return
    }
    const options = contactCategories.map((e: { id: number; name: string }) => ({
      id: `${e.id}`,
      label: e.name,
    }))
    setContactCategoryOptions(options)
    setTierCategoriesSelectOptions(options)
  }, [contactCategoriesEntity])

  useEffect(() => {
    if (!contactStatusesEntity) {
      return
    }
    const { contactStatuses } = contactStatusesEntity.data
    if (!contactStatuses) {
      return
    }
    const options = contactStatuses.map((e: { id: number; name: string }) => ({
      id: `${e.id}`,
      label: e.name,
    }))
    setContactStatusOptions(options)
  }, [contactStatusesEntity])

  useEffect(() => {
    if (!contactTypesEntity) {
      return
    }
    const { contactTypes } = contactTypesEntity.data
    if (!contactTypes) {
      return
    }
    const options = contactTypes.map((e: string) => ({ id: e.toLowerCase(), label: e }))
    setContactTypeOptions(options)
  }, [contactTypesEntity])

  const save = async (data: RedtailSettingsFormValues) => {
    try {
      setIsUpdating(true)
      if (!checkpoint?.data) {
        if (data.inbound.contact.autoSync) {
          await createCheckpoint(checkpointRequest)
        }
      } else {
        await updateCheckpoint({ ...checkpointRequest, active: data.inbound.contact.autoSync })
      }
      const integration = userData?.integrations?.find((i) => i.serviceId === IntegrationServiceId.REDTAIL)
      if (integration) {
        const filters: RedtailInboundContactSyncSettingsFilters = {
          type: (data.inbound.contact.filters.type ?? []).length > 0 ? data.inbound.contact.filters.type : undefined,
          status_id: (data.inbound.contact.filters.status_id ?? []).length > 0 ? data.inbound.contact.filters.status_id : undefined,
          category_id: (data.inbound.contact.filters.category_id ?? []).length > 0 ? data.inbound.contact.filters.category_id : undefined,
        }
        const tierCategories = (data.inbound.contact.tierCategories ?? []).length > 0 ? data.inbound.contact.tierCategories : null
        const serviceConfig = merge(integration.serviceConfig || {}, { inbound: { contact: { filters, tierCategories } } })
        await updateUserIntegration({
          accountId,
          integrationName: integration.serviceId,
          integrationId: integration.id,
          payload: { serviceConfig: removeEmptyItems(serviceConfig) },
        })
      }
    } catch (e) {
      console.error(e)
    } finally {
      setIsUpdating(false)
    }
  }

  const saveAutoSync = async (active: boolean) => {
    try {
      if (!checkpoint?.data) {
        if (active) await createCheckpoint(checkpointRequest)
      } else {
        await updateCheckpoint({ ...checkpointRequest, active })
      }
    } catch (e) {
      console.error(e)
    }
  }

  return {
    autoSync: checkpoint?.data?.active ?? false,
    checkpoint,
    isGetCheckpointFetching,
    userData,
    isSyncDisabled,
    isAccountFetching,
    isGetMeQueryFetching,
    contactCategoryOptions,
    isContactCategoriesFetching,
    contactStatusOptions,
    isContactStatusesFetching,
    contactTypeOptions,
    isContactTypesFetching,
    tierCategoriesSelectOptions,
    isFetching:
      !isUpdating && (isGetMeQueryFetching || isGetCheckpointFetching || isContactCategoriesFetching || isContactTypesFetching || isContactStatusesFetching),
    isSaving: isCreateCheckpointLoading || isUpdateCheckpointLoading || isUpdateUserIntegrationLoading,
    save,
    saveAutoSync,
  }
}

export const useRedtailCalendarSync = () => {
  const accountId = useAppSelector(selectAccountId)
  const { data: userData, isFetching: isGetMeQueryFetching } = useGetMeQuery({ accountId: accountId })
  const [updateUserIntegration, { isLoading: isUpdateUserIntegrationLoading }] = useUpdateUserIntegrationMutation()
  const [isUpdating, setIsUpdating] = useState<boolean>(false)

  const save = async (enabled: boolean) => {
    try {
      setIsUpdating(true)
      const integration = userData?.integrations?.find((i) => i.serviceId === IntegrationServiceId.REDTAIL)
      if (!integration) {
        return
      }
      const serviceConfig = merge(integration.serviceConfig || {}, {
        inbound: {
          calendar: {
            enabled,
          },
        },
      })
      await updateUserIntegration({
        accountId,
        integrationName: integration.serviceId,
        integrationId: integration.id,
        payload: { serviceConfig: removeEmptyItems(serviceConfig) },
      })
    } catch (e) {
      console.error(e)
    } finally {
      setIsUpdating(false)
    }
  }

  const autoSync = userData?.integrations?.find((i) => i.serviceId === IntegrationServiceId.REDTAIL)?.serviceConfig?.inbound?.calendar?.enabled ?? false

  return {
    userData,
    autoSync,
    isFetching: !isUpdating && isGetMeQueryFetching,
    isSaving: isUpdateUserIntegrationLoading,
    save,
  }
}
